import { motion } from 'motion/react';
import type { DocumentReferenceField } from 'types/repository.types';
import ReactMarkdown from 'react-markdown';
import Button from 'components/atoms/Button/Button.component';
import { XIconOutline, CheckIconOutline } from 'utils/icons';
import type { ReactNode } from 'react';

type DocumentRepositoryFieldItemProps = {
  field: DocumentReferenceField;
  onAccept: (field: DocumentReferenceField) => void;
  onReject: (field: DocumentReferenceField) => void;
  isLoading?: boolean;
  title: string;
  description?: string;
  acceptButtonLabel?: string;
  rejectButtonLabel?: string;
  children?: ReactNode;
};

const DocumentRepositoryFieldItem = ({
  field,
  onAccept,
  onReject,
  isLoading = false,
  title,
  description,
  acceptButtonLabel,
  rejectButtonLabel,
  children,
}: DocumentRepositoryFieldItemProps): JSX.Element => {
  const handleButtonClick = (e: React.MouseEvent, callback: () => void) => {
    e.preventDefault();
    e.stopPropagation();
    callback();
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      transition={{ duration: 0.3 }}
      className={
        'flex flex-col gap-1 self-center border border-primary-100 shadow-[0px_1px_3px_0px_rgba(27,27,27,0.05)] rounded-lg py-3 shadow-[0px_1px_2px_0px_rgba(27,27,27,0.10),0px_0px_0px_1px_#E7E7E7] w-full'
      }
      onClick={(e: React.MouseEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
      }}
    >
      <ReactMarkdown className="text-sm font-semibold pb-1.5 px-3">
        {title}
      </ReactMarkdown>

      {/* divider */}
      <div className="w-full h-[1px] bg-primary-100" />

      {description && (
        <ReactMarkdown className="text-sm px-3 pt-1.5">
          {description}
        </ReactMarkdown>
      )}

      <div className="px-3">{children}</div>

      <div className="flex gap-2 mt-2 px-3">
        <Button
          onClick={(e) => handleButtonClick(e, () => onReject(field))}
          structure="light"
          className="flex-1 [&>svg]:!w-5 [&>svg]:!h-5"
          size="xs"
          icon={XIconOutline}
          disabled={isLoading}
          loading={isLoading}
        >
          {rejectButtonLabel}
        </Button>
        <Button
          onClick={(e) => handleButtonClick(e, () => onAccept(field))}
          structure="light"
          className="flex-1 [&>svg]:!w-5 [&>svg]:!h-5"
          color="taxes"
          size="xs"
          icon={CheckIconOutline}
          disabled={isLoading}
          loading={isLoading}
        >
          {acceptButtonLabel}
        </Button>
      </div>
    </motion.div>
  );
};

export default DocumentRepositoryFieldItem;
